import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authErrorMessage } from '../../utils/authErrorMessage';
import AuthShell from './AuthShell';
import AuthHeader from './AuthHeader';
import AuthError from './AuthError';

const RESEND_COOLDOWN_SECONDS = 60;

// P-AUTH-EMAIL-VERIFY-2: the one reusable "please confirm your e-mail"
// screen. Fully self-contained (reads currentUser/auth actions itself via
// useAuth()) so it can be rendered both from Register.js's own post-signup
// step and from App.js's central ProtectedRoute gate without either caller
// having to wire props through. It never decides on its own when
// verification is "done" — the moment currentUser.emailVerified becomes
// true, whichever caller is rendering this simply stops rendering it.
//
// `onSignedOut` is an optional escape hatch for "Utiliser une autre
// adresse": Register.js passes one that resets its own local `step` back to
// 'account' after logout, rather than this component reacting to
// currentUser becoming falsy on its own — currentUser can transiently be
// null for unrelated reasons (e.g. AuthContext's own onAuthStateChange
// re-dispatching a stale initial session read), so treating "no
// currentUser" itself as "the person asked to sign out" would be fragile.
// The default (no prop) navigates to /register, which is correct for the
// central ProtectedRoute gate.
export default function EmailVerificationPending({ onSignedOut } = {}) {
  const { currentUser, logout, resendEmailVerification, refreshEmailVerification } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCheckVerified = async () => {
    if (checking) return;
    setChecking(true);
    setError('');
    setNotice('');
    try {
      const verified = await refreshEmailVerification();
      if (!verified) {
        setNotice("L'adresse n'est pas encore vérifiée. Ouvrez le lien reçu par e-mail, puis réessayez.");
      }
    } catch (err) {
      setError(authErrorMessage(err, 'email-verification'));
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (resending || cooldown > 0) return;
    setResending(true);
    setError('');
    setNotice('');
    try {
      await resendEmailVerification();
      setNotice('E-mail renvoyé. Pensez à vérifier également vos courriers indésirables.');
      startCooldown();
    } catch (err) {
      setError(authErrorMessage(err, 'email-verification'));
    } finally {
      setResending(false);
    }
  };

  const handleUseAnotherAddress = async () => {
    try { await logout(); } catch (e) {}
    if (onSignedOut) {
      onSignedOut();
    } else {
      navigate('/register');
    }
  };

  return (
    <AuthShell>
      <AuthError message={notice} tone="info" />
      <AuthError message={error} tone="error" />
      <AuthHeader eyebrow="VÉRIFICATION REQUISE" title="Vérifiez votre adresse e-mail" />
      <p className="auth-subtitle" style={{ marginBottom: 8 }}>
        Nous avons envoyé un lien de vérification à&nbsp;:
        <strong className="auth-verify-email">{currentUser?.email || 'votre adresse'}</strong>
      </p>
      <p className="auth-subtitle" style={{ marginBottom: 8 }}>
        Ouvrez ce lien pour confirmer que cette adresse vous appartient.
      </p>
      {/* P-AUTH-EMAIL-UX-1: the Spam hint is part of the INITIAL screen —
          visible before the user ever feels the need to resend, and worded
          as calm information, never as an error. */}
      <p className="auth-verify-hint">
        <Mail size={14} aria-hidden="true" />
        <span>Vous ne trouvez pas l'e-mail ? Patientez quelques instants et vérifiez également vos courriers indésirables (Spam).</span>
      </p>
      <div className="auth-stack">
        <button type="button" className="auth-button auth-button-primary" disabled={checking} onClick={handleCheckVerified}>
          {checking ? 'Vérification…' : "J'ai vérifié mon adresse"}
        </button>
        {/* Resend deliberately stays SECONDARY: introduced by a calm line,
            not promoted — a link already received (even in Spam) remains
            usable, so resending is the fallback, not the reflex. */}
        <p className="auth-verify-resend-intro">Vous n'avez toujours rien reçu ?</p>
        <button type="button" className="auth-button auth-button-secondary" disabled={resending || cooldown > 0} onClick={handleResend}>
          {cooldown > 0 ? `Renvoyer dans ${cooldown} s` : (resending ? 'Envoi…' : "Renvoyer l'e-mail")}
        </button>
        <button type="button" className="auth-button auth-button-ghost" onClick={handleUseAnotherAddress}>
          Utiliser une autre adresse
        </button>
      </div>
    </AuthShell>
  );
}
